import { useSeo } from "../lib/useSeo";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ASSETS, LINKS, PILLARS } from "../lib/constants";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, Label, MedicalAccess } from "../components/bits";

const paragraphs = [
  "Welcome to Higher Ground DC. More than a medical cannabis dispensary, Higher Ground DC is a thoughtfully designed space where wellness, education, and lifestyle come together. Located in Washington, DC, we are creating a one-of-a-kind destination centered on a patient-first experience, premium products, and a deep respect for the plant and its culture.",
  "At Higher Ground DC, we believe patients deserve more than a quick transaction. Our approach is rooted in curated cannabis selections, personalized guidance, and an elevated retail experience built on trust, quality, and transparency.",
  "In early spring, Higher Ground DC will expand to include a medical treatment lounge and a sister state-of-the-art, seed-to-sale cultivation facility. This fully integrated ecosystem allows us to maintain the highest standards at every stage, from cultivation to patient care, while ensuring consistency, education, and intentional use.",
  "Whether you're a longtime consumer or new to medical cannabis, Higher Ground DC is a place to feel informed, supported, and at ease. We are committed to delivering carefully curated products, expert guidance, and a level of service defined by care, discretion, and trust.",
];

export default function About() {
  useSeo("/about");
  return (
    <div data-testid="about-page">
      <PageHero
        label="About Us"
        title="Where Cannabis Culture & Community Intersect"
        image={ASSETS.exteriorMural}
        testid="about-hero"
      />

      <section className="bg-ink py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-[1fr_0.9fr] lg:px-12">
          <div className="space-y-6">
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <p className="text-base leading-relaxed text-white/65 lg:text-lg">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.1}>
              <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="about-explore-btn" className="mt-4 inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                Explore Our Products <ArrowUpRight className="h-4 w-4" />
              </a>
            </Reveal>
          </div>
          <div className="space-y-4">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={ASSETS.brandedBags} alt="Higher Ground DC signature packaging" className="h-full w-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative aspect-video overflow-hidden">
                <img src={ASSETS.groupPatio} alt="Higher Ground DC community" className="h-full w-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* A look inside — business gallery */}
      <section className="border-t border-white/10 bg-ink py-20 md:py-28" data-testid="about-gallery">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal><Label>A Look Inside</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
              Our Space, Our People, Our Culture
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3">
            {[
              { src: ASSETS.exteriorWide, label: "1135 Okie St NE", cls: "col-span-2 aspect-[16/9] md:col-span-2 md:row-span-2 md:aspect-auto" },
              { src: ASSETS.counterService, label: "Patient-First Service", cls: "aspect-square" },
              { src: ASSETS.brandedBags, label: "Signature Packaging", cls: "aspect-square" },
              { src: ASSETS.comedyStage, label: "The Lounge & Patio", cls: "aspect-square" },
              { src: ASSETS.soundBath, label: "Community Wellness", cls: "aspect-square" },
              { src: ASSETS.chocPour, label: "Craft Edibles", cls: "aspect-square" },
              { src: ASSETS.groupPatio, label: "Our Community", cls: "aspect-square" },
            ].map((img) => (
              <StaggerItem key={img.src} className={img.cls.includes("col-span-2") ? "col-span-2 md:col-span-2 md:row-span-2" : ""}>
                <div className={`group relative h-full overflow-hidden border border-white/10 ${img.cls}`}>
                  <img src={img.src} alt={img.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end bg-gradient-to-t from-ink via-ink/30 to-transparent p-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <span className="text-xs uppercase tracking-[0.18em] text-gold">{img.label}</span>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Why choose us */}
      <section className="border-t border-white/10 bg-ink-2 py-20 md:py-28" data-testid="about-why">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal><Label>The Higher Ground DC Difference</Label></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">Why Choose Us</h2></Reveal>
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={0.1 * i}>
                <div className="group flex h-full flex-col bg-ink p-8 transition-colors duration-500 hover:bg-ink-3 md:p-12">
                  <span className="font-display text-6xl font-extralight text-gold/30 transition-colors group-hover:text-gold md:text-7xl">{p.n}</span>
                  <h3 className="mt-6 font-display text-xl font-light uppercase tracking-[0.1em] text-white">{p.title}</h3>
                  <p className="mt-4 leading-relaxed text-white/55">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MedicalAccess />
    </div>
  );
}
